import {
  BowlFood,
  Calculator,
  ClipboardText,
  CookingPot,
  ForkKnife,
  Gauge,
  GearSix,
  HardDrives,
  List,
  Moon,
  Package,
  SignOut,
  Sun,
  TrashSimple,
  Truck,
  X,
} from "@phosphor-icons/react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { api } from "../api";
import { formatGermanDisplayText } from "../germanDisplay";
import { applyCustomTheme, applyTheme, getInitialTheme } from "../theme";
import { BrandLogo } from "./BrandLogo";

const navigation = [
  {
    to: "/",
    label: "Übersicht",
    icon: Gauge,
    permissions: [],
  },
  {
    to: "/speiseplanung",
    label: "Speiseplanung",
    icon: ForkKnife,
    permissions: ["speiseplanung.view"],
  },
  {
    to: "/kueche",
    label: "Küche und Rezepturen",
    icon: CookingPot,
    permissions: ["rezepte.view", "menu.view"],
  },
  {
    to: "/lager",
    label: "Artikel und Lager",
    icon: Package,
    permissions: ["lager.view", "lieferanten.view"],
  },
  {
    to: "/einkauf",
    label: "Einkauf",
    icon: ClipboardText,
    permissions: ["einkauf.view", "budget.view", "wareneingang.view"],
  },
  {
    to: "/lmiv",
    label: "LMIV und Kalorien",
    icon: BowlFood,
    permissions: ["lmiv.view", "kalorienref.view"],
  },
  {
    to: "/controlling",
    label: "Controlling",
    icon: Calculator,
    permissions: [
      "conusers.view",
      "conroles.view",
      "conmenu.view",
      "conrezepte.view",
      "conspeiseplanung.view",
      "conkalorienref.view",
      "conlager.view",
      "conlieferanten.view",
      "conwaste.view",
    ],
  },
  {
    to: "/waste",
    label: "Waste Management",
    icon: TrashSimple,
    permissions: [
      "waste.view",
      "waste.capture",
      "waste.kpi",
      "conwaste.view",
    ],
  },
  {
    to: "/abrechnung",
    label: "Abrechnung",
    icon: ClipboardText,
    permissions: ["billing.view"],
  },
  {
    to: "/fuhrpark",
    label: "Fuhrpark",
    icon: Truck,
    permissions: ["fuhrpark.view"],
  },
  {
    to: "/it-service",
    label: "IT-Service",
    icon: HardDrives,
    permissions: [
      "ticketsystem.view",
      "workorder.view",
      "hardware.view",
    ],
  },
  {
    to: "/vertraege",
    label: "Verträge",
    icon: ClipboardText,
    permissions: ["contracts.view"],
  },
  {
    to: "/administration",
    label: "Administration",
    icon: GearSix,
    permissions: [
      "users.view",
      "roles.view",
      "settings.view",
    ],
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const reduced = useReducedMotion();

  const [compact, setCompact] = useState(
    () => localStorage.getItem("cm-sidebar") === "compact",
  );

  const [mobile, setMobile] = useState(false);

  const [dark, setDark] = useState(
    () => getInitialTheme() === "dark",
  );

  const customTheme = useQuery({
    queryKey: ["application-theme"],
    queryFn: () =>
      api<{ values: Record<string, string> }>("/api/theme"),
  });

  useEffect(() => {
    applyTheme(dark ? "dark" : "light");
  }, [dark]);

  useEffect(() => {
    if (customTheme.data) {
      applyCustomTheme(customTheme.data.values);
    }
  }, [customTheme.data]);

  useEffect(() => {
    const shortcuts: Record<
      string,
      {
        to: string;
        permission: string;
      }
    > = {
      "1": {
        to: "/speiseplanung",
        permission: "speiseplanung.view",
      },
      "2": {
        to: "/kueche?tab=menus",
        permission: "menu.view",
      },
      "3": {
        to: "/kueche?tab=recipes",
        permission: "rezepte.view",
      },
      "4": {
        to: "/lager?tab=articles",
        permission: "lager.view",
      },
      "5": {
        to: "/lager?tab=suppliers",
        permission: "lieferanten.view",
      },
      "0": {
        to: "/administration?tab=settings",
        permission: "settings.view",
      },
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        !event.ctrlKey ||
        event.altKey ||
        event.metaKey
      ) {
        return;
      }

      const target = shortcuts[event.key];

      if (
        !target ||
        !auth.can(target.permission)
      ) {
        return;
      }

      event.preventDefault();
      navigate(target.to);
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [auth, navigate]);

  const items = useMemo(
    () =>
      navigation.filter(
        (item) =>
          !item.permissions.length ||
          item.permissions.some(auth.can),
      ),
    [auth],
  );

  const toggleCompact = () => {
    setCompact((value) => {
      localStorage.setItem(
        "cm-sidebar",
        value ? "full" : "compact",
      );

      return !value;
    });
  };

  return (
    <div
      className={`app-shell ${
        compact ? "sidebar-compact" : ""
      }`}
    >
      <header className="mobile-header">
        <button
          className="icon-button"
          onClick={() => setMobile(true)}
          aria-label="Navigation öffnen"
        >
          <List size={22} />
        </button>

        <div className="wordmark">
          <BrandLogo compact />
          <strong>CaterMate</strong>
        </div>

        <button
          className="icon-button"
          onClick={() => setDark((value) => !value)}
          aria-label="Farbschema wechseln"
        >
          {dark ? <Sun /> : <Moon />}
        </button>
      </header>

      <AnimatePresence>
        {mobile && (
          <motion.button
            className="mobile-scrim"
            aria-label="Navigation schließen"
            onClick={() => setMobile(false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>

      <aside
        className={`sidebar ${
          mobile ? "sidebar-mobile-open" : ""
        }`}
      >
        <div className="sidebar-brand">
          <BrandLogo compact />

          <div>
            <strong>CaterMate</strong>
            <span>Betriebsplattform</span>
          </div>

          <button
            className="icon-button mobile-close"
            onClick={() => setMobile(false)}
            aria-label="Navigation schließen"
          >
            <X />
          </button>
        </div>

        <nav>
          {items.map((item) => {
            const Icon = item.icon;

            return (
              <NavLink
                end={item.to === "/"}
                to={item.to}
                key={item.to}
                onClick={() => setMobile(false)}
                title={compact ? item.label : undefined}
                className={({ isActive }) =>
                  isActive
                    ? "nav-item active"
                    : "nav-item"
                }
              >
                <Icon
                  size={21}
                  weight="duotone"
                />

                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <button
            className="nav-item desktop-only"
            onClick={toggleCompact}
          >
            <List size={21} />

            <span>
              {compact
                ? "Navigation öffnen"
                : "Navigation reduzieren"}
            </span>
          </button>

          <button
            className="nav-item"
            onClick={() => setDark((value) => !value)}
          >
            {dark ? (
              <Sun size={21} />
            ) : (
              <Moon size={21} />
            )}

            <span>
              {dark
                ? "Helles Schema"
                : "Dunkles Schema"}
            </span>
          </button>

          <div className="account">
            <div>
              {auth.user?.display_name
                .slice(0, 1)
                .toUpperCase()}
            </div>

            <span>
              <strong>
                {formatGermanDisplayText(
                  auth.user?.display_name || "",
                )}
              </strong>

              <small>
                {formatGermanDisplayText(
                  auth.user?.roles.join(", ") ||
                    "Benutzer",
                )}
              </small>
            </span>

            <button
              className="icon-button"
              onClick={() => auth.logout()}
              aria-label="Abmelden"
            >
              <SignOut />
            </button>
          </div>
        </div>
      </aside>

      <main className="app-main">
        <AnimatePresence
          mode="wait"
          initial={false}
        >
          <motion.div
            key={location.pathname}
            initial={
              reduced
                ? false
                : {
                    opacity: 0,
                    x: 10,
                  }
            }
            animate={{
              opacity: 1,
              x: 0,
            }}
            exit={
              reduced
                ? undefined
                : {
                    opacity: 0,
                    x: -8,
                  }
            }
            transition={{
              duration: 0.18,
            }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}